import NoSleep from '@uriopass/nosleep.js';
import { useEffect, useLayoutEffect, useState } from 'react';

import { useBackground } from '~/modules/elements/background-context';
import Loader from '~/modules/elements/loader';
import { switchToTheme } from '~/modules/game-engine/drawing/styles';
import events from '~/modules/game-events/game-events';
import { useEventEffect, useEventListener } from '~/modules/game-events/hooks';
import useQueryParam from '~/modules/hooks/use-query-param';
import RemoteMicClient from '~/modules/remote-mic/network/client';
import { useClientHandler } from '~/modules/remote-mic/network/client/hooks/use-client-handler';
import { useSubscription } from '~/modules/remote-mic/network/client/hooks/use-subscription';
import isDev from '~/modules/utils/is-dev';
import isE2E from '~/modules/utils/is-e2-e';
import storage from '~/modules/utils/storage';
import BottomBar from '~/routes/remote-mic/bottom-bar';
import ColourSheet from '~/routes/remote-mic/components/colour-sheet';
import { InfoRevealProvider } from '~/routes/remote-mic/components/info-reveal';
import { useInfoReveal } from '~/routes/remote-mic/components/info-reveal-context';
import { PHONE_TABS } from '~/routes/remote-mic/components/phone-tabs';
import { PhoneToastProvider } from '~/routes/remote-mic/components/phone-toast';
import { usePhoneToast } from '~/routes/remote-mic/components/phone-toast-context';
import usePhoneLandscape from '~/routes/remote-mic/hooks/use-phone-landscape';
import useTabSwipe from '~/routes/remote-mic/hooks/use-tab-swipe';
import Microphone from '~/routes/remote-mic/panels/microphone';
import ConfirmReadiness from '~/routes/remote-mic/panels/microphone/confirm-readiness';
import ConnectionWizard from '~/routes/remote-mic/panels/microphone/connection-wizard';
import RemoteSettings from '~/routes/remote-mic/panels/remote-settings';
import RemoteSongList from '~/routes/remote-mic/panels/remote-song-list';
import useSendInitialSongList from '~/routes/remote-mic/panels/remote-song-list/use-send-initial-song-list';
import TopBar from '~/routes/remote-mic/top-bar';
import { AutoEnableFullscreenSetting, useSettingValue } from '~/routes/settings/settings-state';

const noSleep = new NoSleep();

export type ConnectionStatuses = Parameters<typeof events.karaokeConnectionStatusChange.dispatch>[0] | 'uninitialised';

export type PhoneTabs = 'microphone' | 'song-list' | 'settings';

const LOCKED_WHILE_SINGING: PhoneTabs[] = ['song-list', 'settings'];

function RemoteMicShell() {
  const roomId = useQueryParam('room');
  const style = useSubscription('style') ?? 'regular';
  useLayoutEffect(() => {
    switchToTheme(style);
  }, [style]);
  // The phone is its own surface rather than a window onto the app's themed gradient: the blue read
  // as a bar under the connection ribbon and around the panel. `false` rather than covering it up -
  // the gradient is a 15s infinite animation on a fixed full-screen div, and running that under an
  // opaque shell is battery spent on something nobody can see. `switchToTheme` above still runs, so
  // the player colours follow the season.
  useBackground(false, style);

  const [tab, setTab] = useState(0);
  const isLandscape = usePhoneLandscape();
  const flash = usePhoneToast();
  // Anything the television has to say that its mirrored controls cannot - see `flashMessage`.
  useClientHandler('flashMessage', flash);
  const { clear: clearInfo } = useInfoReveal();

  const [connectionStatus, connectionError] = useEventListener(events.karaokeConnectionStatusChange) ?? [
    'uninitialised',
  ];

  const [monitoringStarted, setMonitoringStarted] = useState(false);
  // Turned off by the person holding the phone, in Settings - the only time the top bar says MIC OFF.
  // The television stops listening on screens that need no voice, which is not the phone being muted.
  const [micMuted, setMicMuted] = useState(false);
  useEventEffect(events.micMonitoringStarted, () => {
    setMonitoringStarted(true);
    setMicMuted(false);
  });
  useEventEffect(events.micMonitoringStopped, () => setMonitoringStarted(false));

  const isConnected = connectionStatus === 'connected';
  const [autoEnableFullscreen] = useSettingValue(AutoEnableFullscreenSetting);
  const [wizardComplete, setWizardComplete] = useState(() => isConnected);
  const [isAutoReconnecting, setIsAutoReconnecting] = useState(false);

  // A reload the host asked for skips the join flow entirely and reconnects silently - the phone has
  // already answered every question the wizard asks.
  useEffect(() => {
    if (storage.session.getItem('reload-mic-request') !== null) {
      global.setTimeout(() => storage.session.removeItem('reload-mic-request'), 1);
      setIsAutoReconnecting(true);
      setWizardComplete(true);
      const storedName = storage.local.getItem<string>('remote_mic_name') ?? '';
      RemoteMicClient.connect(roomId ?? '', storedName, true);
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isConnected && isAutoReconnecting) setIsAutoReconnecting(false);
  }, [isConnected, isAutoReconnecting]);

  useSendInitialSongList(connectionStatus === 'connected');

  const [isKeepAwakeOn, setIsKeepAwakeOn] = useState(false);

  const setKeepAwake = async (turnOn: boolean) => {
    try {
      if (turnOn && !noSleep.isEnabled) {
        await noSleep.enable();
      } else if (!turnOn && noSleep.isEnabled) {
        await noSleep.disable();
      }
      setIsKeepAwakeOn(turnOn);
    } catch (e) {
      console.warn("Couldn't set wakelock", e);
    }
  };

  const onWizardComplete = async () => {
    setWizardComplete(true);
    void setKeepAwake(true);

    try {
      if (!isDev() && !isE2E() && autoEnableFullscreen) {
        await document.body.requestFullscreen();
        global.screen.orientation.unlock();
        await global.screen.orientation.lock?.('portrait');
      }
    } catch (e) {
      console.warn(e);
    }
  };

  // While a song runs the phone is the microphone and nothing else: the other two tabs dim, a press
  // on either says why rather than doing nothing, and the swipe is refused so it cannot go round the
  // lock. The cost - nobody can queue the next song until this one ends - was weighed and accepted.
  // Gated on the connection as well: a phone that has dropped cannot be told the song ended, and a
  // phone locked to a tab it cannot leave is worse than one that guesses wrong for a moment.
  const currentSong = useSubscription('current-song');
  const songRunning = isConnected && currentSong !== undefined;

  const goToTab = (next: number) => {
    clearInfo();
    setTab(next);
  };

  const { dx, dragging, headingFor, handlers } = useTabSwipe({
    tab,
    count: PHONE_TABS.length,
    onTabChange: goToTab,
    disabled: songRunning || !wizardComplete,
  });

  const panelProps = {
    roomId,
    monitoringStarted,
    setMonitoringStarted,
    connectionStatus,
    setIsKeepAwakeOn,
    isKeepAwakeOn,
    connectionError,
  };

  const onTabPress = (next: PhoneTabs) => {
    if (next !== 'microphone' && songRunning) {
      flash("You're singing — back when the song ends");
      return;
    }
    goToTab(PHONE_TABS.indexOf(next));
  };

  // The join flow owns the phone until it has joined: no tab bar and no swipe, so there is one thing to
  // do. The song list and Settings are there from the moment it has.
  const showWizard = !wizardComplete;

  const micPanel = showWizard ? (
    <ConnectionWizard
      key="wizard"
      roomId={roomId}
      connectionStatus={connectionStatus}
      connectionError={connectionError}
      onWizardComplete={onWizardComplete}
    />
  ) : isAutoReconnecting && !isConnected ? (
    <div key="reconnecting" className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4">
      <Loader />
      <span className="text-[1.1875rem] font-bold text-white">Reconnecting…</span>
    </div>
  ) : (
    <Microphone key="microphone" {...panelProps} micMuted={micMuted} />
  );

  const panels = [
    micPanel,
    <RemoteSongList key="song-list" {...panelProps} isActive={PHONE_TABS[tab] === 'song-list'} />,
    <RemoteSettings key="settings" {...panelProps} onMicMuted={() => setMicMuted(true)} />,
  ];

  return (
    <>
      <ConfirmReadiness onConfirm={() => setKeepAwake(true)} />
      <ColourSheet joined={wizardComplete} />
      {/* Behind the shell as well as under it: the column is capped at 45rem and centred, so on
          anything wider than a phone the margins either side would otherwise fall through to a
          `body` that sets no background of its own. */}
      <div aria-hidden className="fixed inset-0 -z-10 bg-[#05070b]" />
      <div
        id="phone-ui-container"
        className={`relative mx-auto flex h-dvh w-full flex-col bg-[#05070b] ${isLandscape ? '' : 'max-w-[45rem]'}`}>
        <TopBar
          connectionStatus={connectionStatus}
          roomId={roomId}
          isMicOn={monitoringStarted}
          micMuted={micMuted}
          joined={wizardComplete}
        />
        {isLandscape ? (
          // Landscape keeps the arrangement it already had - a rail beside one mounted panel. The
          // redesign is portrait-only by decision, and a 300%-wide track has nowhere to go beside a
          // vertical rail.
          <div className="flex min-h-0 flex-1 flex-row">
            {wizardComplete && (
              <BottomBar
                layout="rail"
                active={PHONE_TABS[tab]}
                setActiveTab={onTabPress}
                lockedTabs={songRunning ? LOCKED_WHILE_SINGING : []}
                onLockedPress={() => flash("You're singing — back when the song ends")}
                className="order-1"
              />
            )}
            <div className="order-2 flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">{panels[tab]}</div>
          </div>
        ) : (
          <div
            {...handlers}
            data-test="tab-track"
            className="relative min-h-0 flex-1 touch-pan-y overflow-hidden select-none">
            <div
              className="flex h-full w-[300%]"
              style={{
                transform: `translateX(calc(${-tab * 33.3333}% + ${dx}px))`,
                // Cleared while the finger is down so the track follows it rather than easing
                // towards where it was a frame ago; a started swipe is always cancellable.
                transition: dragging ? 'none' : 'transform .32s cubic-bezier(.22,.61,.36,1)',
              }}>
              {panels.map((panel, index) => (
                <div
                  key={PHONE_TABS[index]}
                  data-test={`panel-${PHONE_TABS[index]}`}
                  className="flex h-full w-1/3 min-w-0 flex-col">
                  {panel}
                </div>
              ))}
            </div>
          </div>
        )}
        {!isLandscape && wizardComplete && (
          <BottomBar
            layout="bar"
            active={PHONE_TABS[tab]}
            heading={PHONE_TABS[headingFor]}
            setActiveTab={onTabPress}
            lockedTabs={songRunning ? LOCKED_WHILE_SINGING : []}
            onLockedPress={() => flash("You're singing — back when the song ends")}
          />
        )}
      </div>
    </>
  );
}

export default function RemoteMic() {
  return (
    <PhoneToastProvider>
      <InfoRevealProvider>
        <RemoteMicShell />
      </InfoRevealProvider>
    </PhoneToastProvider>
  );
}
